import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

/**
 * Elevation schematic for `dorm-wardrobe`.
 *
 * Front: top band, base band, horizontal divider, 2 drawer faces in the
 * lower section, 2 door panels in the upper section with vertical pulls
 * near the center seam and hinge indicators on the outer edges.
 * Side: top band + base band with the base recessed from the front edge.
 *
 * Constants match dormWardrobeRenderer.js.
 */

const IN = 0.0254;
const TOP_THICK = 1 * IN;
const BASE_H = 3 * IN;
const BASE_RECESS = 0.75 * IN;
const PANEL_THICK = 0.75 * IN;
const DRAWER_GAP = 0.3 * IN;
const DOOR_GAP = 0.3 * IN;
const NUM_DRAWERS = 2;
const DRAWER_SECTION_H = 12 * IN;
const PULL_H = 3.5 * IN;
const HINGE_H = 3 * IN;

export function DormWardrobeSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <WardrobeSide
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT
  const bodyTop = TOP_THICK;
  const bodyBottom = height - BASE_H;
  const innerW = width - 2 * PANEL_THICK;

  // Drawer section sits between base and divider
  const drawerBottomY = bodyBottom;
  const dividerY = drawerBottomY - DRAWER_SECTION_H;

  // Drawer geometry
  const drawerAvailH = DRAWER_SECTION_H - PANEL_THICK;
  const drawerH = (drawerAvailH - (NUM_DRAWERS - 1) * DRAWER_GAP) / NUM_DRAWERS;

  // Door section
  const doorTopY = bodyTop;
  const doorBottomY = dividerY;
  const doorH = doorBottomY - doorTopY - DOOR_GAP;
  const doorW = (innerW - DOOR_GAP) / 2;
  const doorCenterY = doorTopY + DOOR_GAP / 2 + doorH / 2;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Top band */}
      <Line
        x1={0}
        y1={bodyTop}
        x2={width}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Base band */}
      <Line
        x1={0}
        y1={bodyBottom}
        x2={width}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Horizontal divider between doors and drawers */}
      <Line
        x1={PANEL_THICK}
        y1={dividerY}
        x2={width - PANEL_THICK}
        y2={dividerY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* --- Door section --- */}
      {/* Vertical center seam between doors */}
      <Line
        x1={width / 2}
        y1={doorTopY}
        x2={width / 2}
        y2={dividerY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Door pulls (vertical bars near center seam) */}
      {[-1, 1].map((side) => {
        const pullX = width / 2 + side * (DOOR_GAP / 2 + 1.5 * IN + 0.25 * IN);
        return (
          <Line
            key={`pull-${side}`}
            x1={pullX}
            y1={doorCenterY - PULL_H / 2}
            x2={pullX}
            y2={doorCenterY + PULL_H / 2}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
      {/* Hinge indicators (small marks on outer edges) */}
      {[-1, 1].map((side) => {
        const hingeX = side < 0 ? PANEL_THICK : width - PANEL_THICK;
        const inset = side < 0 ? 1 : -1;
        return [-1, 1].map((vert) => {
          const hingeY = doorCenterY + vert * (doorH / 2 - HINGE_H - 2 * IN);
          return (
            <Line
              key={`hinge-${side}-${vert}`}
              x1={hingeX}
              y1={hingeY}
              x2={hingeX + inset * 1.5 * IN}
              y2={hingeY}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
          );
        });
      })}

      {/* --- Drawer section --- */}
      {/* Drawer gap lines */}
      {Array.from({ length: NUM_DRAWERS - 1 }, (_, g) => {
        const y =
          dividerY +
          PANEL_THICK +
          (g + 1) * drawerH +
          g * DRAWER_GAP +
          DRAWER_GAP / 2;
        return (
          <Line
            key={`drawer-gap-${g}`}
            x1={PANEL_THICK}
            y1={y}
            x2={width - PANEL_THICK}
            y2={y}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        );
      })}
    </SchematicShell>
  );
}

function WardrobeSide({ depth, height, strokeColor, strokeWidth }) {
  const bodyTop = TOP_THICK;
  const bodyBottom = height - BASE_H;

  return (
    <g>
      {/* Top band */}
      <Line
        x1={0}
        y1={bodyTop}
        x2={depth}
        y2={bodyTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Base band — recessed from the front edge */}
      <Line
        x1={0}
        y1={bodyBottom}
        x2={depth - BASE_RECESS}
        y2={bodyBottom}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Vertical edge of base recess */}
      <Line
        x1={depth - BASE_RECESS}
        y1={bodyBottom}
        x2={depth - BASE_RECESS}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

DormWardrobeSchematic.supportsSideViews = true;
